import { z } from 'zod';
import type { RefinementCtx } from 'zod';
import type { PartnershipAdSettingsFields } from '../types';

type PartnershipAdSettingsValidationContext = Pick<RefinementCtx, 'addIssue'>;

const adSettingIdSchema = z.union([
  z.number().int().positive(),
  z.string().trim().min(1, 'Can\'t be empty'),
]);

export const partnershipAdSettingsFieldSchema = z.object({
  page_id: adSettingIdSchema,
  text_id: adSettingIdSchema,
});

export const adSettingsFieldsSchema = z
  .record(partnershipAdSettingsFieldSchema)
  .refine((value) => Object.keys(value).length > 0, {
    message: 'Ad settings are required',
  });

const isValidAdSettingId = (value: unknown): boolean => {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value > 0;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  return false;
};

export const isAdSettingsEntryEmpty = (
  settings: Record<string, unknown> | undefined,
): boolean => {
  if (!settings) {
    return true;
  }

  const keys = Object.keys(settings);
  if (!keys.length) {
    return true;
  }

  return keys.every((key) => {
    const value = settings[key];
    if (!value || typeof value !== 'object') {
      return true;
    }

    const entry = value as Record<string, unknown>;
    return !isValidAdSettingId(entry.page_id) && !isValidAdSettingId(entry.text_id);
  });
};

export const toPartnershipAdSettingsFields = (
  settings: Record<string, unknown> | undefined,
): PartnershipAdSettingsFields => adSettingsFieldsSchema.parse(settings ?? {});

export const coerceAdSettingId = (value: unknown): string | number => {
  if (value === '' || value == null) {
    return '';
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    return Number(value);
  }

  if (typeof value === 'string') {
    return value;
  }

  return '';
};

export const normalizeAdSettingsSlot = (
  slot: Record<string, unknown> | undefined,
): Record<string, { page_id: string | number; text_id: string | number }> => {
  if (!slot) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(slot).map(([flowName, value]) => {
      if (!value || typeof value !== 'object') {
        return [flowName, { page_id: '', text_id: '' }];
      }

      const entry = value as Record<string, unknown>;
      return [
        flowName,
        {
          page_id: coerceAdSettingId(entry.page_id),
          text_id: coerceAdSettingId(entry.text_id),
        },
      ];
    }),
  );
};

export const normalizeAdSettingsByIndex = (
  slots: readonly Record<string, unknown>[] | undefined,
): Record<string, { page_id: string | number; text_id: string | number }>[] =>
  (slots ?? []).map((slot) => normalizeAdSettingsSlot(slot));

export const alignAdSettingsSlots = (
  slots: readonly Record<string, unknown>[] | undefined,
  count: number,
): Record<string, { page_id: string | number; text_id: string | number }>[] => {
  const next = normalizeAdSettingsByIndex(slots);
  while (next.length < Math.max(count, 0)) {
    next.push({});
  }
  return next;
};

export const mergeAdSettingsSlot = (
  defaults: Record<string, { page_id: string | number; text_id: string | number }>,
  existing: Record<string, unknown> | undefined,
): Record<string, { page_id: string | number; text_id: string | number }> => {
  const normalizedExisting = normalizeAdSettingsSlot(existing);
  const flowNames = new Set([
    ...Object.keys(defaults),
    ...Object.keys(normalizedExisting),
  ]);

  return Object.fromEntries(
    [...flowNames].map((flowName) => {
      const defaultEntry = defaults[flowName] ?? { page_id: '', text_id: '' };
      const existingEntry = normalizedExisting[flowName];

      return [
        flowName,
        {
          page_id: existingEntry && isValidAdSettingId(existingEntry.page_id)
            ? existingEntry.page_id
            : defaultEntry.page_id,
          text_id: existingEntry && isValidAdSettingId(existingEntry.text_id)
            ? existingEntry.text_id
            : defaultEntry.text_id,
        },
      ];
    }),
  );
};

export const hasAdSettingsEntries = (
  slots: readonly Record<string, unknown>[] | undefined,
): boolean =>
  (slots ?? []).some((slot) => !isAdSettingsEntryEmpty(slot));

export type AdSettingsFormFieldName = 'packAdSettings' | 'partnershipAdSettingsFields';

export const validateAdSettingsFieldsAtIndex = (
  settings: Record<string, unknown> | undefined,
  index: number,
  ctx: PartnershipAdSettingsValidationContext,
  fieldName: AdSettingsFormFieldName,
): void => {
  const result = adSettingsFieldsSchema.safeParse(settings ?? {});

  if (result.success) {
    return;
  }

  result.error.issues.forEach((issue) => {
    ctx.addIssue({
      code: 'custom',
      message: issue.message,
      path: [fieldName, index, ...issue.path],
    });
  });
};

export const validateAdSettingsForFlowsAtIndex = (
  settings: Record<string, unknown> | undefined,
  index: number,
  ctx: PartnershipAdSettingsValidationContext,
  fieldName: AdSettingsFormFieldName,
  flowNames: readonly string[],
): void => {
  const slot = settings ?? {};

  flowNames.forEach((flowName) => {
    const entry = slot[flowName];
    const result = partnershipAdSettingsFieldSchema.safeParse(
      entry && typeof entry === 'object' ? entry : { page_id: '', text_id: '' },
    );

    if (result.success) {
      return;
    }

    result.error.issues.forEach((issue) => {
      ctx.addIssue({
        code: 'custom',
        message: issue.message,
        path: [fieldName, index, flowName, ...issue.path],
      });
    });
  });
};
